/**
 * Bundled common-password data for the password policy (D-008). Two lists:
 *
 * - `COMMON_PASSWORDS`: frequently leaked passwords that are long enough (≥ 10 characters) to pass
 *   the length rule, compared case-insensitively.
 * - `COMMON_PASSWORD_WORDS`: words that people turn into "Word@123"-style passwords. The policy
 *   strips leading/trailing digits and symbols, undoes common letter substitutions (`@`→a, `0`→o,
 *   `3`→e, `$`→s …) and rejects the password when what is left is one of these words.
 *
 * Curated by hand from widely published breach-frequency lists plus India-specific patterns
 * (city names, deity names, cricket) and company words. Extend it freely; keep entries lowercase.
 */

export const COMMON_PASSWORDS: readonly string[] = [
  '0123456789',
  '0987654321',
  '1029384756',
  '1111111111',
  '1122334455',
  '11223344556677',
  '1212121212',
  '1234512345',
  '123123123123',
  '1234567890',
  '12345678910',
  '1234567890a',
  '123456789a',
  '123456789q',
  '1234567890q',
  '1234qwerasdf',
  '123abc456',
  '123qweasd',
  '123qweasdzxc',
  '12345qwert',
  '12345qwerty',
  '147258369a',
  '1q2w3e4r5t',
  '1q2w3e4r5t6y',
  '1qaz2wsx3edc',
  '1qazxsw23edc',
  '2wsx3edc4rfv',
  '5555555555',
  '7777777777',
  '9876543210',
  'a123456789',
  'aaaaaaaaaa',
  'abc1234567',
  'abcd123456',
  'abcdefghij',
  'asdfghjkl1',
  'asdfghjkl;',
  'iloveyou12',
  'iloveyou123',
  'letmein123',
  'passw0rd123',
  'password12',
  'password123',
  'password1234',
  'q123456789',
  'q1w2e3r4t5',
  'q1w2e3r4t5y6',
  'qazwsxedcr',
  'qazwsxedcrfv',
  'qwe123qwe123',
  'qwerty1234',
  'qwerty12345',
  'qwerty123456',
  'qwertyuiop',
  'qwertyuiop1',
  'qwertyuiop123',
  'trustno1234',
  'welcome123',
  'zaq12wsxcde3',
  'zaq1xsw2cde3',
  'zxcvbnm123',
];

export const COMMON_PASSWORD_WORDS: readonly string[] = [
  // Generic
  'password',
  'passwd',
  'pass',
  'welcome',
  'hello',
  'letmein',
  'iloveyou',
  'loveyou',
  'love',
  'lovely',
  'admin',
  'administrator',
  'root',
  'login',
  'user',
  'guest',
  'test',
  'testing',
  'demo',
  'default',
  'changeme',
  'secret',
  'master',
  'access',
  'whatever',
  'trustno',
  'freedom',
  'sunshine',
  'princess',
  'monkey',
  'dragon',
  'shadow',
  'superman',
  'batman',
  'starwars',
  'pokemon',
  'football',
  'baseball',
  'soccer',
  'hockey',
  'cricket',
  'computer',
  'internet',
  'google',
  'samsung',
  'iphone',
  'apple',
  'banana',
  'orange',
  'cheese',
  'chocolate',
  'cookie',
  'flower',
  'angel',
  'baby',
  'blessed',
  'family',
  'friends',
  'killer',
  'hunter',
  'ranger',
  'tigger',
  'buster',
  'pepper',
  'ginger',
  'summer',
  'winter',
  'spring',
  'autumn',
  'monday',
  'friday',
  'sunday',
  'january',
  'february',
  'march',
  'april',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
  // Keyboard patterns and alphabets
  'qwerty',
  'qwertyuiop',
  'asdf',
  'asdfgh',
  'asdfghjkl',
  'zxcvbnm',
  'qazwsx',
  'qazwsxedc',
  'abc',
  'abcd',
  'abcdef',
  'abcdefgh',
  'abcdefghij',
  // India-specific
  'india',
  'bharat',
  'hindustan',
  'jaihind',
  'mumbai',
  'delhi',
  'newdelhi',
  'noida',
  'gurgaon',
  'gurugram',
  'bangalore',
  'bengaluru',
  'chennai',
  'kolkata',
  'hyderabad',
  'pune',
  'ahmedabad',
  'jaipur',
  'lucknow',
  'krishna',
  'radhekrishna',
  'harekrishna',
  'ganesh',
  'ganesha',
  'shiva',
  'mahadev',
  'harharmahadev',
  'omnamahshivaya',
  'hanuman',
  'jaishreeram',
  'shreeram',
  'sairam',
  'omsairam',
  'saibaba',
  'waheguru',
  'lakshmi',
  'durga',
  'radha',
  'sachin',
  'dhoni',
  'virat',
  'rohit',
  // Company and product words
  'comhard',
  'comskyorbit',
  'comhardtech',
  'comhardtechnologies',
  'company',
  'office',
  'work',
  'hub',
];
